/*
 * The radius-scan helper's arithmetic (docs/GAME_DESIGN.md Part III section
 * 19, PR14): which compass sectors of the search circle a player has faced.
 * Pure; components/events/RadiusScan.js feeds it the device's heading.
 */

export const SECTORS = 8;

/** The heading of a DeviceOrientation event in degrees from north, or null:
 *  iOS gives webkitCompassHeading; elsewhere an absolute alpha counts
 *  anticlockwise, so north-based heading is 360 - alpha. */
export function headingOf(event) {
  if (!event) return null;
  if (Number.isFinite(event.webkitCompassHeading)) return normalise(event.webkitCompassHeading);
  if (event.absolute && Number.isFinite(event.alpha)) return normalise(360 - event.alpha);
  return null;
}

export function normalise(degrees) {
  const d = Number(degrees) % 360;
  return d < 0 ? d + 360 : d;
}

/** The sector (0 = north, clockwise) a heading falls in. */
export function sectorOf(heading, sectors = SECTORS) {
  if (!Number.isFinite(heading)) return null;
  const width = 360 / sectors;
  return Math.floor(normalise(heading + width / 2) / width) % sectors;
}

/** `seen` (an array of booleans) with `heading`'s sector marked. A new array
 *  only when something changed. */
export function mark(seen, heading, sectors = SECTORS) {
  const current = Array.isArray(seen) && seen.length === sectors ? seen : Array(sectors).fill(false);
  const sector = sectorOf(heading, sectors);
  if (sector === null || current[sector]) return current;
  const next = current.slice();
  next[sector] = true;
  return next;
}

/** How much of the circle has been faced, 0..1. */
export function coverage(seen) {
  if (!Array.isArray(seen) || !seen.length) return 0;
  return seen.filter(Boolean).length / seen.length;
}

/** An SVG path for sector `i` of a ring between radii `inner` and `outer`,
 *  centred at (c, c): what the overlay draws. */
export function sectorPath(i, sectors, c, inner, outer) {
  const width = (2 * Math.PI) / sectors;
  const start = i * width - width / 2 - Math.PI / 2;
  const end = start + width;
  const p = (r, a) => `${(c + r * Math.cos(a)).toFixed(2)} ${(c + r * Math.sin(a)).toFixed(2)}`;
  return `M ${p(outer, start)} A ${outer} ${outer} 0 0 1 ${p(outer, end)} L ${p(inner, end)} `
    + `A ${inner} ${inner} 0 0 0 ${p(inner, start)} Z`;
}
